import { debounceTime } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Socket } from "../infrastructure/socket.service";
import { Subject } from "rxjs";
import { UserService } from "../domain/user-service";
import { HttpService } from "../infrastructure/http/http";
import { ChannelInfo } from "../domain/channel";
import { MediaRepository } from "../domain/media-repository";

@Injectable()
export class ReportsService {
  public reportsGlobalState: Subject<ClientReportsState> =
    new Subject<ClientReportsState>();

  constructor(
    private socketService: Socket,
    private userService: UserService,
    private httpService: HttpService,
    private _mediaRepository: MediaRepository,
  ) {
    this.setupListeners();
  }

  public getEmptyContractsReport(channelId: number): Promise<Array<any>> {
    return new Promise((resolve, reject) => {
      const url = `reports/GetEmptyContractsReport?ChannelId=${channelId}`;
      const options = this.httpService.createDefaultRequestOptions();

      this.httpService.getJSON(url, options).then((response) => {
        if (response.success) {
          resolve(response.message);
        } else {
          reject(response.message);
        }
      });
    });
  }

  getAZStatusesReport() {
    return this.httpService.get("reports/GetAZStatusesReport");
  }

  getChannelTargetsReport(channelIds: number[], availabilityZoneIds: number[]) {
    if (channelIds && channelIds.length > 0) {
      return this.httpService.get(
        `reports/GetChannelTargetsReport?channelIdsParam=${channelIds.join(",")}&availabilityZoneIdsParam=${availabilityZoneIds.join(",")}`,
      );
    } else {
      return Promise.resolve([]);
    }
  }
  public getMissingAudiosReport(channelId: number): Promise<Array<any>> {
    return new Promise((resolve, reject) => {
      const url = `reports/GetMissingAudiosReport?ChannelId=${channelId}`;
      const options = this.httpService.createDefaultRequestOptions();

      this.httpService.getJSON(url, options).then((response) => {
        if (response.success) {
          resolve(response.message);
        } else {
          reject(response.message);
        }
      });
    });
  }

  public getMissingSubtitlesReport(channelId: number): Promise<Array<any>> {
    return new Promise((resolve, reject) => {
      const url = `reports/GetMissingSubtitlesReport?ChannelId=${channelId}`;
      const options = this.httpService.createDefaultRequestOptions();

      this.httpService.getJSON(url, options).then((response) => {
        if (response.success) {
          resolve(response.message);
        } else {
          reject(response.message);
        }
      });
    });
  }

  public getFullSegmentsReport(timeCodeFormat: number) {
    return this.httpService.getFile(
      "reports/GetFullSegmentsReport?timeCodeType=" + timeCodeFormat,
      `FullSegmentsReport-${new Date().toLocaleDateString().replace(/\//g, ".")}.csv`,
    );
  }
  public getFullMediaReport() {
    return this.httpService.getFile(
      "reports/GetFullMediaReport",
      `FullMediaReport-${new Date().toLocaleDateString().replace(/\//g, ".")}.csv`,
    );
  }
  public getMediaUsageReport(mediaId: number) {
    this._mediaRepository.getMediaInfo(mediaId).then((mediaInfo) => {
      return this.httpService.getFile(
        `reports/GetMediaUsageReport?mediaId=${mediaId}`,
        `MediaUsageReport-${mediaInfo.tenantMediaId}-${new Date().toLocaleDateString().replace(/\//g, ".")}.csv`,
      );
    });
  }
  public getMediaMetadataReport() {
    return this.httpService.getFile(
      "reports/GetMediaMetadataReport",
      `MediaMetadataReport-${new Date().toLocaleDateString().replace(/\//g, ".")}.csv`,
    );
  }

  public getGapsReport(channelId: number): Promise<Array<any>> {
    return new Promise((resolve, reject) => {
      const url = `reports/GetGapsReport?ChannelId=${channelId}`;
      const options = this.httpService.createDefaultRequestOptions();

      this.httpService.getJSON(url, options).then((response) => {
        if (response.success) {
          resolve(response.message);
        } else {
          reject(response.message);
        }
      });
    });
  }

  public updateAlertsSettings(updatedAlertsSettings: AlertsSettings): void {
    console.log("ReportsService: updateAlertsSettings");

    this.socketService.reportsHubConnection.invoke(
      "updateAlertsSettings",
      updatedAlertsSettings,
    );
  }
  public getSchedulerAlerts(
    reportTypesFilter: Array<number>,
    reportIncludeChannels: Array<number>,
    utc_now_timestamp: number,
    utc_to_timestamp: number,
    output: string = "json",
  ) {
    //console.log('getSchedulerAlerts-> ',this.dateFrom.getTi,this.dateTo)
    return this.httpService.post(`reports/getscheduleralertsbytenant`, {
      from: utc_now_timestamp,
      to: utc_to_timestamp,
      reportTypes: reportTypesFilter,
      includeChannelIds: reportIncludeChannels,
      output: output,
    });
  }

  private setupListeners() {
    this.userService.getCurrentChannel().then((channel) => {
      if (channel) {
        console.log(`Starting to listen to channel  ${channel.id}`);
        this.startListeningForReports(channel.id);
      }
    });

    this.userService.currentChannelIdUpdated
      .pipe(debounceTime(2000))
      .subscribe((channelId) => {
        console.log(`Starting to listen to channel  ${channelId}`);

        this.startListeningForReports(channelId);
      });

    this.socketService.reportsHubConnection.on(
      "updateReportsState",
      this.onUpdateReportsState.bind(this),
    );
  }

  private onUpdateReportsState(newReportsStateDto) {
    this.reportsGlobalState.next(
      ClientReportsState.createFromDto(newReportsStateDto),
    );
  }

  private startListeningForReports(channelId: number) {
    this.socketService.reportsHubConnection
      .invoke("StartListeningForReports", channelId)
      .then(() => {})
      .catch((error) => console.log(error));
  }
}

export class ClientReportsState {
  public gapsCount: number;
  public missingSubtitlesCount: number;
  public missingAudiosCount: number;
  public emptyContractCount: number;

  public reportAlertsSettings: AlertsSettings;

  public static createFromDto(dto): ClientReportsState {
    if (dto) {
      return Object.assign({}, new ClientReportsState(), dto);
    } else {
      return null;
    }
  }
}

export class AlertsSettings {
  public channelId: number;
  public gapsReportEnabled: boolean;
  public missingSubtitleReportEnabled: boolean;

  public missingAudiosReportEnabled: boolean;
  public emptyContractReportEnabled: boolean;
  public alertsTimespanInDays = 7;

  constructor() {}
}
